import { useQuery } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";

import { MASTER_INVALIDATION_KEY } from "@/api/queryClient";
import type { UserPermissions } from "@/api/resources/auth/types";
import { apiConfigWorkspaces, createWorkspace, deleteWorkspace, updateWorkspace } from "@/api/resources/workspaces/api";
import { workspacesQueries, workspacesQueryKeys } from "@/api/resources/workspaces/queries";
import { selectorDirectAccessWorkspaces, selectorWorkspacesByKey } from "@/api/resources/workspaces/selectors";
import i18n from "@/providers/i18n";
import { spawnCreateMutationHook, spawnDeleteMutationHook, spawnUpdateMutationHook } from "@/utils/api";
import { type QueryFilter } from "@/utils/filter";

const entity = i18n.t("workspace");

export const useWorkspaces = () => {
  return useQuery(workspacesQueries.listAll());
};

export const useWorkspacesByKey = () => {
  return useQuery({ ...workspacesQueries.listAll(), select: selectorWorkspacesByKey });
};

export const useWorkspace = (workspace_id?: string) => {
  return useQuery(workspacesQueries.byId(workspace_id));
};

export const useCurrentWorkspace = () => {
  const { workspace_id } = useParams({ strict: false });
  return useQuery(workspacesQueries.byId(workspace_id));
};

export const useDirectAccessWorkspaces = (userPermissions?: UserPermissions | null) => {
  return useQuery({
    ...workspacesQueries.listAll(),
    select: (data) => selectorDirectAccessWorkspaces(data, userPermissions),
  });
};

export const useCurrentCustomerWorkspaces = () => {
  const { workspace_id } = useParams({ strict: false });
  return useQuery(workspacesQueries.listCustomerWorkspaces(workspace_id));
};

export const useWorkspacesQueryFilters = () => {
  const { data: currentWorkspace } = useCurrentWorkspace();
  return {
    currentWorkspace: currentWorkspace ? (["workspace_id", "eq", currentWorkspace.id] as QueryFilter) : undefined,
  };
};

export const useCreateWorkspace = spawnCreateMutationHook({
  mutationFn: createWorkspace,
  notifyEntity: entity,
  analyticsEntity: apiConfigWorkspaces.table,
  invalidationQueryKeys: [MASTER_INVALIDATION_KEY],
});
export const useUpdateWorkspace = (id?: string) =>
  spawnUpdateMutationHook({
    mutationFn: updateWorkspace,
    notifyEntity: entity,
    analyticsEntity: apiConfigWorkspaces.table,
    entityQueryKey: workspacesQueryKeys.byId(id),
    invalidationQueryKeys: [workspacesQueryKeys.lists()],
  })();

export const useDeleteWorkspace = spawnDeleteMutationHook({
  mutationFn: deleteWorkspace,
  notifyEntity: entity,
  analyticsEntity: apiConfigWorkspaces.table,
  invalidationQueryKeys: [MASTER_INVALIDATION_KEY],
});
